/* ZINKE — Kontrastwächter, 18.09.2026
   ====================================

   Anlass: Auf der Seite „Sendung verfolgen" (App ParcelPanel) standen die
   Ereignisse in hellgrauen Kästen — mit der hellen Schrift des Themes darin.
   Also weiß auf weiß, nicht zu lesen.

   Ursache: Fremde Inhalte (Apps, Anwendungs-Proxy-Seiten, App-Blöcke) bringen
   ihre eigenen Hintergrundfarben mit, meist für helle Shops gebaut. Die
   Schriftfarbe erben sie dagegen vom Theme — und das ist hier Creme auf
   Schwarz. Das Theme kann fremdes CSS nicht vorhersehen, deshalb prüft dieses
   Skript das Ergebnis.

   Was es tut: Es geht die Seite durch, rechnet für jede Stelle mit eigenem
   Text den echten Kontrast aus (Schriftfarbe gegen den tatsächlich sichtbaren
   Hintergrund, Transparenz eingerechnet) und färbt die Schrift um, wenn das
   Verhältnis unter 3,2:1 liegt:

     heller Hintergrund  -> dunkle Schrift (#16161a), Links in dunklem Cognac
     dunkler Hintergrund -> helle Schrift (#f2efe8)

   Es greift nur ein, wo es sonst unlesbar wäre; alles andere bleibt, wie es
   gestaltet ist. Wer eine Stelle bewusst so haben will, setzt dort das
   Attribut data-kontrast-aus. Jede Stelle wird nur einmal geprueft.

   Nachzügler (Apps laden ihren Inhalt oft später) fängt ein MutationObserver
   ab, gedrosselt auf höchstens einen Durchlauf alle 300 ms. */
(() => {
  'use strict';

  const GRENZE = 3.2;            // Kontrastverhältnis, ab dem eingegriffen wird
  const SEITENFARBE = { r: 14, g: 14, b: 14 };   // --paper, Rückfall ganz hinten
  const UEBERSPRINGEN = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEMPLATE', 'IFRAME', 'OPTION', 'BR', 'HR', 'CANVAS', 'VIDEO']);

  const stil = document.createElement('style');
  stil.textContent =
    '.zinke-kontrast-hell{color:#16161a !important}' +
    '.zinke-kontrast-hell a,a.zinke-kontrast-hell{color:#7a5320 !important}' +
    '.zinke-kontrast-dunkel{color:#f2efe8 !important}' +
    '.zinke-kontrast-dunkel a,a.zinke-kontrast-dunkel{color:#d8bd86 !important}';
  (document.head || document.documentElement).appendChild(stil);

  function zahlen(farbtext) {
    const treffer = String(farbtext || '').match(/rgba?\(([^)]+)\)/);
    if (!treffer) return null;
    const t = treffer[1].split(/[,\s/]+/).filter(Boolean).map(Number);
    if (t.length < 3 || t.some(Number.isNaN)) return null;
    return { r: t[0], g: t[1], b: t[2], a: t.length > 3 ? t[3] : 1 };
  }

  function mischen(vorn, hinten) {
    const a = vorn.a;
    return {
      r: vorn.r * a + hinten.r * (1 - a),
      g: vorn.g * a + hinten.g * (1 - a),
      b: vorn.b * a + hinten.b * (1 - a),
      a: 1
    };
  }

  function helligkeit(f) {
    const k = v => { v /= 255; return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); };
    return 0.2126 * k(f.r) + 0.7152 * k(f.g) + 0.0722 * k(f.b);
  }

  function verhaeltnis(a, b) {
    const hoch = Math.max(a, b) + 0.05;
    const tief = Math.min(a, b) + 0.05;
    return hoch / tief;
  }

  // Sichtbarer Hintergrund: von der Stelle nach oben, bis eine deckende Farbe kommt.
  function hintergrund(el) {
    const schichten = [];
    let n = el;
    while (n && n.nodeType === 1) {
      const f = zahlen(getComputedStyle(n).backgroundColor);
      if (f && f.a > 0) {
        schichten.push(f);
        if (f.a >= 0.999) break;
      }
      n = n.parentElement;
    }
    let unten = { r: SEITENFARBE.r, g: SEITENFARBE.g, b: SEITENFARBE.b, a: 1 };
    for (let i = schichten.length - 1; i >= 0; i--) unten = mischen(schichten[i], unten);
    return unten;
  }

  function hatEigenenText(el) {
    if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT') return true;
    for (const kind of el.childNodes) {
      if (kind.nodeType === 3 && kind.nodeValue.trim()) return true;
    }
    return false;
  }

  const gesehen = new WeakSet();

  function pruefen() {
    const stellen = document.body ? document.body.querySelectorAll('*') : [];
    let geprueft = 0;
    for (const el of stellen) {
      if (geprueft > 6000) break;
      if (gesehen.has(el)) continue;
      if (UEBERSPRINGEN.has(el.tagName) || el.namespaceURI !== 'http://www.w3.org/1999/xhtml') continue;
      if (el.closest('[data-kontrast-aus]')) continue;
      if (!hatEigenenText(el)) continue;

      const stil = getComputedStyle(el);
      if (stil.visibility === 'hidden' || stil.display === 'none' || stil.opacity === '0') continue;
      geprueft++;

      /* Nicht anfassen, wo die Schriftfarbe bewusst durchsichtig ist: der
         Name „Pascal" im Journal ist ein Farbverlauf, der durch die Buchstaben
         scheint (background-clip: text), die Ziffern der Favoriten sind nur
         umrandet. Eine Schriftfarbe daraufzusetzen würde beides zerstören. */
      const fuellung = stil.webkitTextFillColor || stil.color;
      const clip = (stil.webkitBackgroundClip || stil.backgroundClip || '');
      if (clip.indexOf('text') !== -1) { gesehen.add(el); continue; }
      const fuellwert = zahlen(fuellung);
      if (fuellwert && fuellwert.a === 0) { gesehen.add(el); continue; }

      const grund = hintergrund(el);
      const schriftRoh = zahlen(stil.color);
      if (!schriftRoh) continue;
      const schrift = schriftRoh.a < 1 ? mischen(schriftRoh, grund) : schriftRoh;

      gesehen.add(el);
      if (verhaeltnis(helligkeit(schrift), helligkeit(grund)) >= GRENZE) continue;

      el.classList.add(helligkeit(grund) > 0.45 ? 'zinke-kontrast-hell' : 'zinke-kontrast-dunkel');
    }
  }

  let takt = null;
  function anstossen() {
    if (takt) return;
    takt = setTimeout(() => { takt = null; pruefen(); }, 300);
  }

  function starten() {
    pruefen();
    // Apps laden ihren Inhalt oft erst nach dem Seitenaufbau nach.
    if ('MutationObserver' in window) {
      new MutationObserver(anstossen).observe(document.body, { childList: true, subtree: true });
    }
    window.addEventListener('load', anstossen);
    setTimeout(anstossen, 1500);
    setTimeout(anstossen, 4000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', starten);
  } else {
    starten();
  }
})();
